/**
 * MODULE: _shared/frontend-shell/src/layout/ShellTopBarInsetContext.tsx
 * PURPOSE: DOM target in TopBar for route-level controls (portal from feature slices).
 * DEPENDENCIES: react
 * PUBLIC_API: ShellTopBarInsetProvider, ShellTopBarInsetSlot, useShellTopBarInset
 * SIDE_EFFECTS: Registers div ref in TopBar beforeAvatar slot
 * AGENT_SAFETY: 🟡 RESTRICTED
 * FILE_SIZE: 55 / 200 lines
 * RECENT: 2026-06-08 [2.2.1335] — Pages page insights dropdown portals here on compact viewports.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

type ShellTopBarInsetContextValue = {
  target: HTMLDivElement | null;
  registerTarget: (node: HTMLDivElement | null) => void;
};

const ShellTopBarInsetContext = createContext<ShellTopBarInsetContextValue | null>(null);

export function ShellTopBarInsetProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<HTMLDivElement | null>(null);
  const registerTarget = useCallback((node: HTMLDivElement | null) => {
    setTarget(node);
  }, []);
  const value = useMemo(() => ({ target, registerTarget }), [target, registerTarget]);
  return (
    <ShellTopBarInsetContext.Provider value={value}>{children}</ShellTopBarInsetContext.Provider>
  );
}

export function useShellTopBarInsetTarget(): HTMLDivElement | null {
  const ctx = useContext(ShellTopBarInsetContext);
  if (!ctx) {
    throw new Error('useShellTopBarInsetTarget must be used within ShellTopBarInsetProvider');
  }
  return ctx.target;
}

/** Mount point in TopBar `beforeAvatar` — feature slices portal controls here. */
export function ShellTopBarInsetSlot() {
  const ctx = useContext(ShellTopBarInsetContext);
  if (!ctx) {
    throw new Error('ShellTopBarInsetSlot must be used within ShellTopBarInsetProvider');
  }
  return (
    <div
      ref={ctx.registerTarget}
      className="flex items-center"
      data-testid="shell-topbar-inset"
    />
  );
}
